/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import { Checkbox } from "@material-ui/core";
import {
  AllInclusive,
  ArrowDownward
} from "@material-ui/icons";
import ReactTooltip from 'react-tooltip';
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Row
} from "reactstrap";
import Toastr from "toastr";
import UploadDropzone from "../components/UploadDropzone";
import { useSaveBulkQnaMutation } from "../hooks/useSaveBulkQnaMutation";
import {BulkQnaModelDisplayName} from './../types';

const BulkQnaPage: React.FC = () => {
  const [name, setName] = useState<string>("");
  const [inputFile, setInputFile] = useState<File>();
  const [modelType, setModelType] = useState<string>("");
  const [openaiModelPreference, setOpenaiModelPreference] = useState<BulkQnaModelDisplayName>(BulkQnaModelDisplayName.gpt53)

  const {
    mutate: saveBulkQna,
    data: saveBulkQnaResponse,
    isLoading: saveBulkQnaLoading,
    isError: saveBulkQnaError,
  } = useSaveBulkQnaMutation();

  const handleSubmit = (selectedType: string) => {
    if (!name) {
      alert("Please enter a name");
      return;
    }

    if (!inputFile) {
      alert("Please upload a file");
      return;
    }

    setModelType(selectedType);    
  };

  useEffect(() => {
    if(modelType !== "" && inputFile ){
      saveBulkQna({
        file: inputFile,
        name,
        modelType,
        openaiModelPreference
      })
      setModelType("")
    }
  }, [modelType, inputFile])
  
  useEffect(() => {
    if (saveBulkQnaError) {
      Toastr.error("Something went wrong. Please try again later");
    }
  }, [saveBulkQnaError])

  useEffect(() => {
    if (saveBulkQnaResponse) {
      Toastr.success("Bulk QnA started! Please check bulk qna execution page for status");
      setInputFile(undefined);
    }
  }, [saveBulkQnaResponse])

  const handleDownloadSample = () => {
    window.open("/bulktemplate.xlsx");
  }

  return (
    <>
      <div className="header pb-6 pt-5 pt-md-8"
        style={{
          height: "500px",
          backgroundColor: '#2D315C',
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundImage: "url(/background-header-infinity.jpg)"
        }}
      >
        {/* Mask */}
        <span className="mask bg-gradient-default opacity-8" />
        <Container style={{ display: "flex", flexDirection: "column" }} fluid>
          <Row>
            <Col>
              <div style={{ textAlign: "center" }}>
                <div style={{ width: "25%", marginBottom: '10px', marginLeft: 'auto', marginRight: 'auto' }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div style={{ display: "inline-block", width: "75%", marginBottom: '10px' }}>
                  <UploadDropzone
                    isFileUploaded={!!saveBulkQnaResponse}
                    setFile={setInputFile}
                    accept={[
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                      'text/csv',
                    ]}
                    icon="/file-types-excel.png"
                    title="Click/drag and drop Excel/CSV file."
                  />
                </div>
              </div>
              <div className="text-center">
              {[BulkQnaModelDisplayName.gpt53].map((model, openaimodelindex) => 
                    <>
                      <Checkbox
                        key={openaimodelindex}
                        checked={openaiModelPreference === model}
                        onChange={() => setOpenaiModelPreference(model)}
                        name="openai-model-name"
                        inputProps={{ "aria-label": "primary checkbox" }}
                        id={`openai-model-name-${openaimodelindex}`}
                        style={{ color: "#fff" }}
                      />
                      <label htmlFor={`openai-model-name-${openaimodelindex}`} style={{ color: "white" }}>{model}</label>
                    </>
                  )}
                </div>
              <div className="text-center">
                {saveBulkQnaLoading ? (
                  <svg height="70px" viewBox="0 0 187.3 93.7" preserveAspectRatio="xMidYMid meet" >
                    <path stroke="rgb(215, 61, 50)" id="outline" fill="none" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10"
                      d="M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z" />
                    <path id="outline-bg" opacity="0.05" fill="none" stroke="#fff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10"
                      d="				M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z" />
                  </svg>
                ) : (
                  <>
                    <Button
                      color="primary"
                      style={{
                        borderRadius: "50%",
                        width: '25px',
                        height: '25px',
                        padding: '0',
                      }}
                      data-tip="Download Sample"
                      onClick={handleDownloadSample}
                    >
                      <ArrowDownward 
                        style={
                          {
                            fontSize: '20px',
                            color: '#fff',
                            padding: '0',
                            margin: '0',
                          }
                        }
                      />
                    </Button>
                    <ReactTooltip place="top" type="dark" effect="float" />
                    <Button
                      color="primary"
                      onClick={() => handleSubmit("Infinity")}
                      disabled={saveBulkQnaLoading}
                      style={{
                        width: "130px", fontSize: "13px", background: "#FF5252",
                        border: "#FF5252"
                      }}
                      className="btn-sm "
                    >
                      Ask Question
                    </Button>
                    
                    {/* <Button
                      color="primary"
                      onClick={() => handleSubmit("NotInfinity")}
                      disabled={saveBulkQnaLoading}
                      style={{
                        width: "130px", fontSize: "13px", background: "#FF5252",
                        border: "#FF5252"
                      }}
                      className="btn-sm "
                    >
                      Ask Internal LLM
                    </Button> */}
                  </>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default BulkQnaPage;
